import React, { useState, useEffect, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { Search, X, Users, Plus, Lock } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import SecurityUtils from '../../utils/security';

const MyGroupsView = () => {
  const { groups, setGroups, users, currentUser, setView, showAlert } = useContext(AppContext);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: '', description: '', isPrivate: false });
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('my-groups');
  const [allGroups, setAllGroups] = useState([]);
  const [loadingDiscover, setLoadingDiscover] = useState(false);

  const [groupNameError, setGroupNameError] = useState('');

  useEffect(() => {
    if (activeTab === 'discover') {
      setLoadingDiscover(true);
      firestoreService.getAllGroups().then(g => {
        setAllGroups(g);
        setLoadingDiscover(false);
      }).catch(() => setLoadingDiscover(false));
    }
  }, [activeTab]);

  // Groups user is NOT a member of, not invited to, and hasn't requested to join
  // Private groups are hidden from discover
  const discoverableGroups = allGroups.filter(g =>
    !g.isPrivate &&
    !g.members?.includes(currentUser.id) &&
    !g.pendingInvites?.includes(currentUser.id) &&
    !g.joinRequests?.includes(currentUser.id)
  );

  // Groups user has requested to join
  const requestedGroups = allGroups.filter(g =>
    g.joinRequests?.includes(currentUser.id)
  );

  const validateGroupName = async (name) => {
    if (!name.trim()) return 'Group name is required';
    if (name.length > 25) return 'Group name must be 25 characters or less';
    if (!/^[a-zA-Z0-9 ]+$/.test(name)) return 'Only letters, numbers, and spaces allowed';
    // Check against all groups for uniqueness
    try {
      const allGroupsList = await firestoreService.getAllGroups();
      if (allGroupsList.some(g => g.name.toLowerCase() === name.trim().toLowerCase())) {
        return 'A group with this name already exists';
      }
    } catch (err) {
      // Fallback to local check
      if (groups.some(g => g.name.toLowerCase() === name.trim().toLowerCase())) {
        return 'A group with this name already exists';
      }
    }
    return '';
  };

  const handleCreateGroup = async () => {
    const error = await validateGroupName(groupForm.name);
    if (error) { setGroupNameError(error); return; }
    setCreating(true);
    try {
      const groupData = {
        name: groupForm.name.trim(),
        description: SecurityUtils.sanitizeText(groupForm.description),
        createdBy: currentUser.id,
        members: [currentUser.id],
        isPrivate: groupForm.isPrivate,
      };
      await firestoreService.createGroup(groupData);
      setShowCreateGroup(false);
      setGroupForm({ name: '', description: '', isPrivate: false });
      setGroupNameError('');
    } catch (err) {
      console.error('Error creating group:', err);
      showAlert('Failed to create group.', 'Error');
    }
    setCreating(false);
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-white">Groups</h2>
        <button onClick={() => setShowCreateGroup(true)} className="flex items-center space-x-2 px-2 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d]">
          <Plus className="w-5 h-5" />
          <span>Create</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 mb-4 bg-gray-900 rounded-lg p-1">
        <button onClick={() => setActiveTab('my-groups')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${activeTab === 'my-groups' ? 'bg-[#16a34a] text-pure-white' : 'text-gray-400 hover:text-white'}`}>My Groups</button>
        <button onClick={() => setActiveTab('discover')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${activeTab === 'discover' ? 'bg-[#16a34a] text-pure-white' : 'text-gray-400 hover:text-white'}`}>Discover</button>
      </div>

      {activeTab === 'my-groups' && (
        <>
          {groups.length === 0 ? (
            <div className="bg-black rounded-lg shadow border border-gray-700 p-8 text-center">
              <Users className="w-12 h-12 text-white mx-auto mb-3" />
              <h3 className="text-lg font-medium text-white mb-2">No groups yet</h3>
              <p className="text-white">Create a group or discover existing ones!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {groups.map(group => {
                const memberUsers = group.members?.map(id => users.find(u => u.id === id)).filter(Boolean) || [];
                const hasPendingInvite = group.pendingInvites?.includes(currentUser.id);
                return (
                  <button key={group.id} onClick={() => setView(`group-detail-${group.id}`)} className={`bg-black rounded-lg shadow border p-4 text-left transition-colors ${hasPendingInvite ? 'border-[#16a34a] ring-1 ring-[#16a34a]' : 'border-gray-700 hover:border-[#16a34a]'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                          <h3 className="text-lg font-bold text-white">{group.name}</h3>
                          {group.isPrivate && <span className="flex items-center space-x-1 text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full"><Lock className="w-3 h-3" /><span>Private</span></span>}
                          {hasPendingInvite && <span className="text-xs bg-[#16a34a] text-pure-white px-2 py-0.5 rounded-full">Invited</span>}
                        </div>
                        {group.description && <p className="text-sm text-gray-400 mt-1">{group.description}</p>}
                      </div>
                      <div className="flex items-center space-x-1 text-gray-400">
                        <Users className="w-4 h-4" />
                        <span className="text-sm">{memberUsers.length}</span>
                      </div>
                    </div>
                    <div className="flex -space-x-2 mt-3">
                      {memberUsers.slice(0, 5).map(u => (
                        <img key={u.id} src={u.profilePicture} alt={u.name} className="w-8 h-8 rounded-full border-2 border-black" />
                      ))}
                      {memberUsers.length > 5 && <span className="w-8 h-8 rounded-full bg-gray-700 border-2 border-black flex items-center justify-center text-xs text-white">+{memberUsers.length - 5}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {activeTab === 'discover' && (
        <>
          {loadingDiscover ? (
            <div className="text-center py-8"><p className="text-gray-400">Loading groups...</p></div>
          ) : (
            <>
              {requestedGroups.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-white mb-3">Pending Requests</h3>
                  <div className="grid grid-cols-1 gap-3">
                    {requestedGroups.map(group => {
                      const memberUsers = group.members?.map(id => users.find(u => u.id === id)).filter(Boolean) || [];
                      return (
                        <button key={group.id} onClick={() => setView(`group-detail-${group.id}`)} className="bg-black rounded-lg shadow border border-yellow-600 p-4 text-left transition-colors hover:border-yellow-500">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center space-x-2">
                                <h3 className="text-lg font-bold text-white">{group.name}</h3>
                                <span className="text-xs bg-yellow-600 text-pure-white px-2 py-0.5 rounded-full">Requested</span>
                              </div>
                              {group.description && <p className="text-sm text-gray-400 mt-1">{group.description}</p>}
                            </div>
                            <div className="flex items-center space-x-1 text-gray-400">
                              <Users className="w-4 h-4" />
                              <span className="text-sm">{memberUsers.length}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {discoverableGroups.length === 0 && requestedGroups.length === 0 ? (
                <div className="bg-black rounded-lg shadow border border-gray-700 p-8 text-center">
                  <Search className="w-12 h-12 text-white mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-white mb-2">No groups to discover</h3>
                  <p className="text-white">You're already in all available groups, or none exist yet.</p>
                </div>
              ) : discoverableGroups.length > 0 && (
                <>
                  <h3 className="text-lg font-bold text-white mb-3">{requestedGroups.length > 0 ? 'Other Groups' : 'All Groups'}</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {discoverableGroups.map(group => {
                      const memberUsers = group.members?.map(id => users.find(u => u.id === id)).filter(Boolean) || [];
                      return (
                        <button key={group.id} onClick={() => setView(`group-detail-${group.id}`)} className="bg-black rounded-lg shadow border border-gray-700 p-4 text-left transition-colors hover:border-[#16a34a]">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-bold text-white">{group.name}</h3>
                              {group.description && <p className="text-sm text-gray-400 mt-1">{group.description}</p>}
                            </div>
                            <div className="flex items-center space-x-1 text-gray-400">
                              <Users className="w-4 h-4" />
                              <span className="text-sm">{memberUsers.length}</span>
                            </div>
                          </div>
                          <div className="flex -space-x-2 mt-3">
                            {memberUsers.slice(0, 5).map(u => (
                              <img key={u.id} src={u.profilePicture} alt={u.name} className="w-8 h-8 rounded-full border-2 border-black" />
                            ))}
                            {memberUsers.length > 5 && <span className="w-8 h-8 rounded-full bg-gray-700 border-2 border-black flex items-center justify-center text-xs text-white">+{memberUsers.length - 5}</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {showCreateGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-black border border-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Create Group</h3>
              <button onClick={() => setShowCreateGroup(false)} className="text-white hover:text-gray-300"><X className="w-6 h-6" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Group Name *</label>
                <input type="text" value={groupForm.name} maxLength={25} onChange={e => { const val = e.target.value.replace(/[^a-zA-Z0-9 ]/g, ''); setGroupForm({ ...groupForm, name: val }); setGroupNameError(''); }} className={`w-full px-3 py-2 border rounded-lg text-white bg-black focus:ring-2 focus:ring-[#16a34a] ${groupNameError ? 'border-red-500' : 'border-gray-700'}`} placeholder="Weekend Golfers" />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-red-400">{groupNameError}</span>
                  <span className={`text-xs ${groupForm.name.length > 20 ? 'text-yellow-400' : 'text-gray-500'}`}>{groupForm.name.length}/25</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Description</label>
                <textarea value={groupForm.description} onChange={e => setGroupForm({ ...groupForm, description: e.target.value })} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-white bg-black focus:ring-2 focus:ring-[#16a34a]" rows="3" placeholder="A group for weekend golfers..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Privacy</label>
                <div className="flex space-x-2">
                  <button type="button" onClick={() => setGroupForm({ ...groupForm, isPrivate: false })} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${!groupForm.isPrivate ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'}`}>Public</button>
                  <button type="button" onClick={() => setGroupForm({ ...groupForm, isPrivate: true })} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-1 ${groupForm.isPrivate ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'}`}><Lock className="w-4 h-4" /><span>Private</span></button>
                </div>
                <p className="text-xs text-gray-500 mt-1">{groupForm.isPrivate ? 'Only the group name is visible to non-members. Joining requires approval.' : 'Anyone can discover and request to join this group.'}</p>
              </div>
              <button onClick={handleCreateGroup} disabled={!groupForm.name.trim() || creating} className="w-full py-2 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] disabled:opacity-50">
                {creating ? 'Creating...' : 'Create Group'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyGroupsView;
